import React from 'react';
import { KTCard, KTCardBody } from '../../../../_metronic/helpers';
import { useGetReportDataQuery, useGetMonthlyActiveSubscriptionsQuery, useGetExpiringSubscriptionsQuery, MonthlyActiveSubscriptionData, ExpiringSubscriptionData } from '../../../store/services/apuestesAPI';
import { User, Metric } from '@apuestes/types';

const AnalyticsAdditionalCards: React.FC = () => {
  
  // Query para highest-profitability
  const { data: profitabilityData, isLoading: profitabilityLoading, error: profitabilityError } = useGetReportDataQuery({
    reportType: 'highest-profitability',
    isPremium: true
  });

  // Query para highest-accuracy
  const { data: accuracyData, isLoading: accuracyLoading, error: accuracyError } = useGetReportDataQuery({
    reportType: 'highest-accuracy',
    isPremium: true
  });

  // Query para highest-benefits
  const { data: benefitsData, isLoading: benefitsLoading, error: benefitsError } = useGetReportDataQuery({
    reportType: 'highest-benefits',
    isPremium: true
  });

  // Query para monthly active subscriptions
  const { data: monthlyActiveData, isLoading: monthlyActiveLoading, error: monthlyActiveError } = useGetMonthlyActiveSubscriptionsQuery();

  // Query para expiring subscriptions
  const { data: expiringData, isLoading: expiringLoading, error: expiringError } = useGetExpiringSubscriptionsQuery();

  // Función para procesar datos de profitability
  const processProfitabilityData = (): Array<{
    name: string;
    profitability: number;
    profitabilityChange: string;
    position: number;
    medal: 'gold' | 'silver' | 'bronze' | null;
  }> => {
    if (!profitabilityData || !Array.isArray(profitabilityData) || profitabilityData.length === 0) {
      return [];
    }

    // Crear una copia del array y ordenar por metrics.ranking, luego tomar los primeros 4 usuarios
    const sortedUsers = [...profitabilityData].sort((a, b) => {
      const rankingA = a.metrics?.ranking || a.metrics?.position || 0;
      const rankingB = b.metrics?.ranking || b.metrics?.position || 0;
      return rankingA - rankingB; // Orden ascendente (menor ranking = mejor posición)
    });
    const topUsers = sortedUsers.slice(0, 4);
    
    return topUsers.map((user: User & { metrics: Metric }, index: number) => {
      const tipster = user.alias || user.firstname || user.email;
      const metrics = user.metrics || {};
      
      // Asignar medalla según posición
      let medal: 'gold' | 'silver' | 'bronze' | null = null;
      if (index === 0) medal = 'gold';
      else if (index === 1) medal = 'silver';
      else if (index === 2) medal = 'bronze';
      
      return {
        name: tipster || 'Usuario',
        profitability: metrics.profit || 0,
        profitabilityChange: metrics.winRate + '%',
        position: index + 1,
        medal
      };
    });
  };

  const topUsers = processProfitabilityData();

  // Función para procesar datos de accuracy
  const processAccuracyData = (): Array<{
    name: string;
    accuracy: number;
    accuracyChange: string;
    position: number;
    medal: 'gold' | 'silver' | 'bronze' | null;
  }> => {
    if (!accuracyData || !Array.isArray(accuracyData) || accuracyData.length === 0) {
      return [];
    }

    // Crear una copia del array y ordenar por metrics.ranking, luego tomar los primeros 4 usuarios
    const sortedUsers = [...accuracyData].sort((a, b) => {
      const rankingA = a.metrics?.ranking || a.metrics?.position || 0;
      const rankingB = b.metrics?.ranking || b.metrics?.position || 0;
      return rankingA - rankingB; // Orden ascendente (menor ranking = mejor posición)
    });
    const topUsers = sortedUsers.slice(0, 4);
    
    return topUsers.map((user: User & { metrics: Metric }, index: number) => {
      const tipster = user.alias || user.firstname || user.email;
      const metrics = user.metrics || {};
      
      // Asignar medalla según posición
      let medal: 'gold' | 'silver' | 'bronze' | null = null;
      if (index === 0) medal = 'gold';
      else if (index === 1) medal = 'silver';
      else if (index === 2) medal = 'bronze';
      
      return {
        name: tipster || 'Usuario',
        accuracy: metrics.winRate || 0,
        accuracyChange: metrics.winRate + '%',
        position: index + 1,
        medal
      };
    });
  };

  const topAccuracyUsers = processAccuracyData();

  // Función para procesar datos de benefits
  const processBenefitsData = (): Array<{
    name: string;
    benefits: number;
    benefitsChange: string;
    position: number;
    medal: 'gold' | 'silver' | 'bronze' | null;
  }> => {
    if (!benefitsData || !Array.isArray(benefitsData) || benefitsData.length === 0) {
      return [];
    }

    // Crear una copia del array y ordenar por metrics.ranking, luego tomar los primeros 4 usuarios
    const sortedUsers = [...benefitsData].sort((a, b) => {
      const rankingA = a.metrics?.ranking || a.metrics?.position || 0;
      const rankingB = b.metrics?.ranking || b.metrics?.position || 0;
      return rankingA - rankingB; // Orden ascendente (menor ranking = mejor posición)
    });
    const topUsers = sortedUsers.slice(0, 4);
    
    return topUsers.map((user: User & { metrics: Metric }, index: number) => {
      const tipster = user.alias || user.firstname || user.email;
      const metrics = user.metrics || {};
      
      // Asignar medalla según posición
      let medal: 'gold' | 'silver' | 'bronze' | null = null;
      if (index === 0) medal = 'gold';
      else if (index === 1) medal = 'silver';
      else if (index === 2) medal = 'bronze';
      
      return {
        name: tipster || 'Usuario',
        benefits: metrics.profit || 0,
        benefitsChange: metrics.yieldLevelStake + '%',
        position: index + 1,
        medal
      };
    });
  };

  const topBenefitsUsers = processBenefitsData();

  // Función para procesar datos de suscripciones activas mensuales
  const processMonthlyActiveData = (): Array<{
    month: string;
    billing: number;
    change: number;
    changeType: 'increase' | 'decrease' | 'neutral';
  }> => {
    if (!monthlyActiveData || !Array.isArray(monthlyActiveData) || monthlyActiveData.length === 0) {
      return [];
    }

    return monthlyActiveData.map((item: MonthlyActiveSubscriptionData, index: number) => {
      const previousValue = index > 0 ? monthlyActiveData[index - 1].billing || 0 : 0;
      const currentValue = item.billing || 0;
      const change = previousValue > 0 ? ((currentValue - previousValue) / previousValue) * 100 : 0;
      
      return {
        month: item.month || `Mes ${index + 1}`,
        billing: currentValue,
        change: Math.abs(change),
        changeType: (change > 0 ? 'increase' : change < 0 ? 'decrease' : 'neutral') as 'increase' | 'decrease' | 'neutral'
      };
    }).slice(-6); // Mostrar solo los últimos 6 meses
  };

  const monthlyActiveSubscriptions = processMonthlyActiveData();

  // Función para procesar datos de suscripciones que expiran
  const processExpiringData = (): Array<{
    id: number;
    userName: string;
    subscriptionName: string;
    endDate: string;
    daysLeft: number;
    status: string;
    isUrgent: boolean;
  }> => {
    if (!expiringData || !Array.isArray(expiringData) || expiringData.length === 0) {
      return [];
    }

    return expiringData.map((item: ExpiringSubscriptionData) => {
      const endDate = new Date(item.end_date);
      const today = new Date();
      const timeDiff = endDate.getTime() - today.getTime();
      const daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));
      
      return {
        id: item.id,
        userName: item.user?.alias || item.user?.firstname || item.user?.email || 'Usuario',
        subscriptionName: item.subscription?.name || item.subscription?.title || 'Suscripción',
        endDate: endDate.toLocaleDateString('es-ES'),
        daysLeft: Math.max(0, daysLeft),
        status: item.status,
        isUrgent: daysLeft <= 7
      };
    }).slice(0, 6); // Mostrar solo las primeras 6
  };

  const expiringSubscriptions = processExpiringData();

  // Función para obtener el icono de medalla
  const getMedalIcon = (medal: 'gold' | 'silver' | 'bronze' | null) => {
    switch (medal) {
      case 'gold':
        return 'fas fa-medal text-warning';
      case 'silver':
        return 'fas fa-medal text-secondary';
      case 'bronze':
        return 'fas fa-medal text-danger';
      default:
        return 'fas fa-circle text-gray-400';
    }
  };

  // Función para obtener el color de fondo de la medalla
  const getMedalBgColor = (medal: 'gold' | 'silver' | 'bronze' | null) => {
    switch (medal) {
      case 'gold':
        return 'bg-light-warning';
      case 'silver':
        return 'bg-light-secondary';
      case 'bronze':
        return 'bg-light-danger';
      default:
        return 'bg-light-gray-200';
    }
  };

  // Función para obtener el color del cambio
  const getChangeColor = (changeType: 'increase' | 'decrease' | 'neutral') => {
    switch (changeType) {
      case 'increase':
        return 'text-success';
      case 'decrease':
        return 'text-danger';
      default:
        return 'text-gray-500';
    }
  };

  // Función para obtener el icono del cambio
  const getChangeIcon = (changeType: 'increase' | 'decrease' | 'neutral') => {
    switch (changeType) {
      case 'increase':
        return 'fas fa-arrow-up';
      case 'decrease':
        return 'fas fa-arrow-down';
      default:
        return 'fas fa-minus';
    }
  };

  // Función para obtener el color de urgencia
  const getUrgencyColor = (isUrgent: boolean, daysLeft: number) => {
    if (isUrgent) return 'text-danger';
    if (daysLeft <= 30) return 'text-warning';
    return 'text-success';
  };

  // Función para obtener el icono de urgencia
  const getUrgencyIcon = (isUrgent: boolean, daysLeft: number) => {
    if (isUrgent) return 'fas fa-exclamation-triangle';
    if (daysLeft <= 30) return 'fas fa-clock';
    return 'fas fa-check-circle';
  };

  if (profitabilityLoading || accuracyLoading || benefitsLoading || monthlyActiveLoading || expiringLoading) {
    return (
      <div className="row g-4">
        <div className="col-12">
          <KTCard className="h-100">
            <KTCardBody className="d-flex flex-center p-6">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            </KTCardBody>
          </KTCard>
        </div>
      </div>
    );
  }

  if (profitabilityError || accuracyError || benefitsError || monthlyActiveError || expiringError) {
    return (
      <div className="row g-4">
        <div className="col-12">
          <KTCard className="h-100">
            <KTCardBody className="d-flex flex-center p-6">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle text-warning fs-1 mb-3"></i>
                <div className="text-muted">Error al cargar los datos</div>
              </div>
            </KTCardBody>
          </KTCard>
        </div>
      </div>
    );
  }

  return (
    <div className="row g-4">
      {/* Highest Profitability Card */}
      <div className="col-lg-4 col-md-6">
        <KTCard className="h-100">
          <KTCardBody className="p-6">
            <div className="d-flex flex-column h-100">
              <div className="d-flex align-items-center mb-6">
                <div className="symbol symbol-50px me-3 bg-light-success">
                  <i className="fas fa-chart-line fs-2x text-success"></i>
                </div>
                <div className="d-flex flex-column">
                  <div className="fs-6 fw-semibold text-gray-400">Top Rentabilidad</div>
                  <div className="fs-7 fw-semibold text-gray-500">Ranking de usuarios</div>
                </div>
              </div>
              
              <div className="flex-grow-1">
                {topUsers.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="fas fa-chart-line fs-3x text-gray-300 mb-3"></i>
                    <div>Sin datos disponibles</div>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {topUsers.map((user, index) => (
                      <div key={index} className="d-flex align-items-center">
                        <div className={`symbol symbol-40px me-3 ${getMedalBgColor(user.medal)}`}>
                          <i className={`${getMedalIcon(user.medal)} fs-5`}></i>
                        </div>
                        <div className="d-flex flex-column flex-grow-1">
                          <div className="fs-7 fw-bold text-gray-800">{user.name}</div>
                          <div className="fs-8 text-gray-500">
                            #{user.position} • {user.profitabilityChange}
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="fs-7 fw-bold text-success">
                            {user.profitability.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </KTCardBody>
        </KTCard>
      </div>

      {/* Highest Accuracy Card */}
      <div className="col-lg-4 col-md-6">
        <KTCard className="h-100">
          <KTCardBody className="p-6">
            <div className="d-flex flex-column h-100">
              <div className="d-flex align-items-center mb-6">
                <div className="symbol symbol-50px me-3 bg-light-primary">
                  <i className="fas fa-bullseye fs-2x text-primary"></i>
                </div>
                <div className="d-flex flex-column">
                  <div className="fs-6 fw-semibold text-gray-400">Top Precisión</div>
                  <div className="fs-7 fw-semibold text-gray-500">Ranking de usuarios</div>
                </div>
              </div>
              
              <div className="flex-grow-1">
                {topAccuracyUsers.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="fas fa-bullseye fs-3x text-gray-300 mb-3"></i>
                    <div>Sin datos disponibles</div>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {topAccuracyUsers.map((user, index) => (
                      <div key={index} className="d-flex align-items-center">
                        <div className={`symbol symbol-40px me-3 ${getMedalBgColor(user.medal)}`}>
                          <i className={`${getMedalIcon(user.medal)} fs-5`}></i>
                        </div>
                        <div className="d-flex flex-column flex-grow-1">
                          <div className="fs-7 fw-bold text-gray-800">{user.name}</div>
                          <div className="fs-8 text-gray-500">
                            #{user.position} • {user.accuracyChange}
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="fs-7 fw-bold text-primary">
                            {user.accuracy.toLocaleString()}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </KTCardBody>
        </KTCard>
      </div>

      {/* Highest Benefits Card */}
      <div className="col-lg-4 col-md-6">
        <KTCard className="h-100">
          <KTCardBody className="p-6">
            <div className="d-flex flex-column h-100">
              <div className="d-flex align-items-center mb-6">
                <div className="symbol symbol-50px me-3 bg-light-info">
                  <i className="fas fa-gift fs-2x text-info"></i>
                </div>
                <div className="d-flex flex-column">
                  <div className="fs-6 fw-semibold text-gray-400">Top Beneficios</div>
                  <div className="fs-7 fw-semibold text-gray-500">Ranking de usuarios</div>
                </div>
              </div>
              
              <div className="flex-grow-1">
                {topBenefitsUsers.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="fas fa-gift fs-3x text-gray-300 mb-3"></i>
                    <div>Sin datos disponibles</div>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {topBenefitsUsers.map((user, index) => (
                      <div key={index} className="d-flex align-items-center">
                        <div className={`symbol symbol-40px me-3 ${getMedalBgColor(user.medal)}`}>
                          <i className={`${getMedalIcon(user.medal)} fs-5`}></i>
                        </div>
                        <div className="d-flex flex-column flex-grow-1">
                          <div className="fs-7 fw-bold text-gray-800">{user.name}</div>
                          <div className="fs-8 text-gray-500">
                            #{user.position} • {user.benefitsChange}
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="fs-7 fw-bold text-info">
                            ${user.benefits.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </KTCardBody>
        </KTCard>
      </div>

      {/* Monthly Active Subscriptions Card */}
      <div className="col-lg-6 col-md-6">
        <KTCard className="h-100">
          <KTCardBody className="p-6">
            <div className="d-flex flex-column h-100">
              <div className="d-flex align-items-center mb-6">
                <div className="symbol symbol-50px me-3 bg-light-warning">
                  <i className="fas fa-chart-line fs-2x text-warning"></i>
                </div>
                <div className="d-flex flex-column">
                  <div className="fs-6 fw-semibold text-gray-400">Facturación Mensual</div>
                  <div className="fs-7 fw-semibold text-gray-500">Últimos 6 meses</div>
                </div>
              </div>
              
              <div className="flex-grow-1">
                {monthlyActiveSubscriptions.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="fas fa-chart-line fs-3x text-gray-300 mb-3"></i>
                    <div>Sin datos disponibles</div>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {monthlyActiveSubscriptions.map((item, index) => (
                      <div key={index} className="d-flex align-items-center">
                        <div className="symbol symbol-40px me-3 bg-light-gray-200">
                          <i className="fas fa-calendar-alt fs-5 text-gray-600"></i>
                        </div>
                        <div className="d-flex flex-column flex-grow-1">
                          <div className="fs-7 fw-bold text-gray-800">{item.month}</div>
                          <div className="fs-8 text-gray-500">
                            ${item.billing.toLocaleString()}
                          </div>
                        </div>
                        <div className="text-end">
                          <div className={`fs-7 fw-bold ${getChangeColor(item.changeType)}`}>
                            <i className={`${getChangeIcon(item.changeType)} me-1`}></i>
                            {item.change.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </KTCardBody>
        </KTCard>
      </div>

      {/* Expiring Subscriptions Card */}
      <div className="col-lg-6 col-md-6">
        <KTCard className="h-100">
          <KTCardBody className="p-6">
            <div className="d-flex flex-column h-100">
              <div className="d-flex align-items-center mb-6">
                <div className="symbol symbol-50px me-3 bg-light-danger">
                  <i className="fas fa-exclamation-triangle fs-2x text-danger"></i>
                </div>
                <div className="d-flex flex-column">
                  <div className="fs-6 fw-semibold text-gray-400">Suscripciones por Expirar</div>
                  <div className="fs-7 fw-semibold text-gray-500">Próximas a vencer</div>
                </div>
              </div>
              
              <div className="flex-grow-1">
                {expiringSubscriptions.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="fas fa-check-circle fs-3x text-gray-300 mb-3"></i>
                    <div>No hay suscripciones próximas a expirar</div>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {expiringSubscriptions.map((item, index) => (
                      <div key={index} className="d-flex align-items-center">
                        <div className="symbol symbol-40px me-3 bg-light-gray-200">
                          <i className={`${getUrgencyIcon(item.isUrgent, item.daysLeft)} fs-5 ${getUrgencyColor(item.isUrgent, item.daysLeft)}`}></i>
                        </div>
                        <div className="d-flex flex-column flex-grow-1">
                          <div className="fs-7 fw-bold text-gray-800">{item.userName}</div>
                          <div className="fs-8 text-gray-500">
                            {item.subscriptionName} • {item.endDate}
                          </div>
                        </div>
                        <div className="text-end">
                          <div className={`fs-7 fw-bold ${getUrgencyColor(item.isUrgent, item.daysLeft)}`}>
                            {item.daysLeft} días
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </KTCardBody>
        </KTCard>
      </div>
    </div>
  );
};

export { AnalyticsAdditionalCards };
